import {configureStore} from "@reduxjs/toolkit";
import logger from "redux-logger";
import {combineReducers} from "redux";
import {offline} from "@redux-offline/redux-offline";
import offlineConfig from "@redux-offline/redux-offline/lib/defaults";
import localForage from "localforage";

import {
  appSettingsSlice,
  authSlice,
  biomarkersSlice,
  blogsSlice,
  healthProgrammesSlice,
  ordersSlice,
  partnersSlice,
  resultsSlice,
  subscriptionsSlice,
  syncSlice,
  testsSlice,
  userSlice,
} from "@/features";
import {encryptPersistData} from "@/helpers/encryptPersistData";
import {
  appSettingsInitialState,
  authInitialState,
  biomarkersInitialState,
  blogsInitialState,
  healthProgrammesInitialState,
  ordersInitialState,
  partnersInitialState,
  resultsInitialState,
  subscriptionsInitialState,
  syncInitialState,
  testsInitialState,
  userInitialState,
} from "@/features/initialStates";

localForage.config({
  name: "Forth",
  description: "persistent storage",
});

const APP_DEBUG = window.config.isDev;

const configForOffline = {
  ...offlineConfig,
  persistOptions: {
    // Include here the reducer name if you don't want to persist the data
    blacklist: ["offline", "_persist"],
    keyPrefix: "offline:",
    transforms: [encryptPersistData],
    storage: localForage,
  },
};

const appReducer = combineReducers({
  auth: authSlice,
  appSettings: appSettingsSlice,
  biomarkers: biomarkersSlice,
  blogs: blogsSlice,
  healthProgrammes: healthProgrammesSlice,
  orders: ordersSlice,
  partners: partnersSlice,
  results: resultsSlice,
  subscriptions: subscriptionsSlice,
  sync: syncSlice,
  tests: testsSlice,
  user: userSlice,
});

const reducers = (state, action) => {
  if (action.type === "auth/logout") {
    const initialGlobalState = {
      appSettings: appSettingsInitialState,
      auth: authInitialState,
      biomarkers: biomarkersInitialState,
      blogs: blogsInitialState,
      healthProgrammes: healthProgrammesInitialState,
      orders: ordersInitialState,
      partners: partnersInitialState,
      results: resultsInitialState,
      subscriptions: subscriptionsInitialState,
      sync: syncInitialState,
      tests: testsInitialState,
      user: userInitialState,
    };

    return appReducer(initialGlobalState, action);
  }

  return appReducer(state, action);
};

export const store = configureStore({
  reducer: reducers,
  middleware: getDefaultMiddleware => {
    if (APP_DEBUG) return getDefaultMiddleware({immutableCheck: false}).concat(logger);
    return getDefaultMiddleware();
  },
  enhancers: getDefaultEnhancers => getDefaultEnhancers().concat(offline(configForOffline)),
  devTools: APP_DEBUG && {
    name: "Forth DevTools",
  },
});

// For unit testing
export const setupStore = preloadedState => {
  return configureStore({
    reducer: reducers,
    middleware: getDefaultMiddleware => getDefaultMiddleware().concat(logger),
    enhancers: getDefaultEnhancers => getDefaultEnhancers().concat(offline(configForOffline)),
    devTools: APP_DEBUG,
    preloadedState,
  });
};
